import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import ReCAPTCHA from 'react-google-recaptcha';
import { routePaths } from '../../../router/paths';
import { currentLang } from '../../../../../utils/lang';
import Title from '../../../components/Title';
import Alert from '../../../../../components/Alert';
import { questionFormSchema } from './validation';
import Field from '../../../../../components/Field';
import FieldInput from '../../../../../components/FieldInput';
import FieldTextArea from '../../../../../components/FieldTextArea';
import FieldSelect from '../../../../../components/FieldSelect';
import { FORM_PHONE_PREFIX } from '../../../../../constants/form';
import { makeSelectValuesFromEnum } from '../../../../../utils/form';
import { QuestionMessageSubject } from '../../../types/question';
import { getElementAttribute } from '../../../utils/element';
import { postQuestion } from '../../../api/api';
const SubmitQuestion = () => {
    const phonePrefix = FORM_PHONE_PREFIX;
    const { t } = useTranslation();
    const currentLocale = currentLang();
    const recaptchaRef = useRef(null);
    const titleRef = useRef(null);
    const messageSubjects = makeSelectValuesFromEnum(QuestionMessageSubject);
    const recaptchaSiteKey = getElementAttribute('data-recaptcha-site-key');
    const [isLoading, setIsLoading] = useState(false);
    const [isGeneralErrorVisible, setIsGeneralErrorVisible] = useState(false);
    const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
    const { setValue, handleSubmit, register, formState, getValues } = useForm({
        // @ts-ignore
        resolver: yupResolver(questionFormSchema),
        mode: 'onChange',
        defaultValues: {
            name: '',
            surname: '',
            email: '',
            messageSubject: '',
            message: '',
        },
        criteriaMode: 'all',
    });
    const onSubmit = () => {
        recaptchaRef.current.execute();
    };
    const submitForm = (captcha) => {
        const form = getValues();
        if (!form.phone) {
            delete form.phone;
        }
        setIsLoading(true);
        setIsGeneralErrorVisible(false);
        postQuestion({ ...form, ...(form.phone && { phone: `${phonePrefix}${form.phone}` }), captcha })
            .then(() => {
            setIsSubmitSuccess(true);
            setIsGeneralErrorVisible(false);
            window.dataLayer.push({
                event: 'formSupportSubmitted',
                formSupportSelectedSubject: form.messageSubject,
            });
        })
            .catch(() => {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
            titleRef.current.parentElement.parentElement.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
            setIsGeneralErrorVisible(true);
            window.dataLayer.push({
                event: 'systemError',
                errorType: 'form support',
                errorMessage: 'internal server error',
            });
        })
            .finally(() => {
            setIsLoading(false);
        });
    };
    const onCaptchaVerified = (captcha) => {
        console.log('onCaptchaVerified');
        recaptchaRef.current.reset();
        submitForm(captcha || '');
    };
    const onCaptchaExpired = () => {
        console.log('onCaptchaExpired');
        recaptchaRef.current.reset();
    };
    return (_jsxs("div", { children: [isSubmitSuccess && (_jsxs("div", { className: "text-center my-5", children: [_jsx("div", { className: "mb-3 pb-3", children: _jsx("img", { src: require('../../../../../../img/icons/check.svg'), alt: t('store_locator.question.form.success_icon_alt') }) }), _jsx("h2", { className: "font-size-extra-large", children: t('store_locator.question.success_title') }), _jsx("p", { className: "font-size-small mb-3 pb-3", children: t('store_locator.question.success_description') }), _jsx(Link, { to: routePaths.Main[currentLocale], className: "btn btn-block btn-md-inline btn-outline-dark mb-4", children: t('store_locator.question.continue') })] })), !isSubmitSuccess && (_jsxs(_Fragment, { children: [_jsx("div", { ref: titleRef, children: _jsx(Title, { title: t('store_locator.question.title') }) }), isGeneralErrorVisible && _jsx(Alert, { type: "error", description: t('store_locator.question.form.general_error') }), _jsxs("form", { onSubmit: handleSubmit(onSubmit), children: [_jsx(Field, { name: "name", isRequired: true, label: t('store_locator.question.form.name'), error: formState.errors.name, children: _jsx(FieldInput, { name: "name", placeholder: t('store_locator.question.form.name'), isValid: !formState.errors.name, register: register('name') }) }), _jsx(Field, { name: "surname", isRequired: true, label: t('store_locator.question.form.surname'), error: formState.errors.surname, children: _jsx(FieldInput, { name: "surname", placeholder: t('store_locator.question.form.name'), isValid: !formState.errors.surname, register: register('surname') }) }), _jsx(Field, { name: "email", isRequired: true, label: t('store_locator.question.form.email'), error: formState.errors.email, children: _jsx(FieldInput, { name: "email", placeholder: t('store_locator.question.form.email'), isValid: !formState.errors.email, register: register('email') }) }), _jsx(Field, { name: "phone", prepend: FORM_PHONE_PREFIX, label: t('store_locator.question.form.phone'), error: formState.errors.phone, children: _jsx(FieldInput, { name: "phone", placeholder: t('store_locator.question.form.phone.placeholder'), showPlaceholder: true, isValid: !formState.errors.phone, register: register('phone') }) }), _jsx(Field, { name: "messageSubject", isRequired: true, label: t('store_locator.question.form.message_subject'), error: formState.errors.messageSubject, children: _jsx(FieldSelect, { name: "messageSubject", label: t('store_locator.question.form.message_subject'), options: messageSubjects, isValid: !formState.errors.messageSubject, register: register('messageSubject'), setValue: setValue }) }), _jsx(Field, { name: "message", isRequired: true, label: t('store_locator.question.form.message'), error: formState.errors.message, children: _jsx(FieldTextArea, { name: "message", placeholder: t('store_locator.question.form.message'), isValid: !formState.errors.message, register: register('message') }) }), _jsx(ReCAPTCHA, { ref: recaptchaRef, size: "invisible", hl: currentLocale, sitekey: recaptchaSiteKey, onChange: onCaptchaVerified, onExpired: onCaptchaExpired }), _jsxs("button", { type: "submit", className: `btn btn-outline-dark btn-block btn-md-inline mb-4 ${isLoading ? 'loading' : ''}`, disabled: isLoading, children: [isLoading && _jsx("span", { className: "spinner-border spinner-border-sm", role: "status", "aria-hidden": "true" }), _jsx("span", { className: isLoading ? 'text-transparent' : '', children: t('store_locator.question.form.submit') })] })] })] }))] }));
};
export default SubmitQuestion;
